'use client';

import { Fragment, useMemo, JSX } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { FormProvider } from 'react-hook-form';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useMedia } from 'react-use';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';

import { breakpoints, modules } from '@/app.config';
import AmediatekaSecondarySVG from '@/icons/AmediatekaSecondarySVG';
import RemoveSVG from '@/icons/RemoveSVG';
import RizanovaSecondarySVG from '@/icons/RizanovaSecondarySVG';
import StartSecondarySVG from '@/icons/StartSecondarySVG';
import WinkSecondarySVG from '@/icons/WinkSecondarySVG';

import {
  findInitialValue,
  useFilterMovieContext,
} from '@/views/FilteredMoviesView/contexts/useFilterMovieContext';

import { FiltersRowsEnum } from '@/enums/filters-rows.enums';
import { MovieCardTypeEnum } from '@/enums/movie-card-type.enums';

import { Button } from '@/components/Buttons';
import { Form } from '@/components/Form';
import { FormCheckbox } from '@/components/Form/FormCheckbox';
import { Option } from '@/components/Form/Select/context/SelectProvider';
import { MoviesTopPanel } from '@/components/MoviesTopPanel';
import { SectionHeading } from '@/components/SectionHeading';
import { Tabs } from '@/components/Tabs';

import styles from '@/views/FilteredMoviesView/PersistableFiltersForm.module.scss';

// =================================================================

const MAX_SELECTABLE_OPTIONS = 3;
const COUNTRY_MAX_SELECTABLE_OPTIONS = 6;

// =================================================================

interface PersistableFiltersFormProps {
  containerClassName?: string;
}

// =================================================================

const cinemasLogo = (cinema: MovieCardTypeEnum): JSX.Element | undefined => {
  switch (cinema) {
    case MovieCardTypeEnum.AMEDIATIKA:
      return <AmediatekaSecondarySVG />;
    case MovieCardTypeEnum.RIZANOVA:
      return <RizanovaSecondarySVG />;
    case MovieCardTypeEnum.START:
      return <StartSecondarySVG />;
    case MovieCardTypeEnum.MORETV:
      return <WinkSecondarySVG />;
    default:
      break;
  }
};

// =================================================================

export const PersistableFiltersForm = (props: PersistableFiltersFormProps) => {
  const { containerClassName } = props;

  const t = useTranslations();
  const searchParams = useSearchParams();

  const isExtraLarge = useMedia(breakpoints.extraLarge, true);

  const currentSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
  const query = currentSearchParams.toString();

  const {
    methods,
    onReset,
    handleChange,
    countries,
    genres,
    qualities,
    cinemas,
    isLoading,
    generateRatingOptions,
    yearOptions,
  } = useFilterMovieContext();

  const moviesModules = useMemo(
    () => modules.filter(module => module.moduleType === 'movies'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modules],
  );

  return (
    <Fragment>
      <SectionHeading as="h1" className="mb-3">
        {t('common.filter')}
      </SectionHeading>
      <FormProvider {...methods}>
        <div className={styles.filterWrapper}>
          <Form
            id="filter-movie"
            isSubmitting={methods.formState.isSubmitting}
            className={clsx(styles.filters, containerClassName)}
          >
            <Col xs={12} className={clsx(styles.scrollable, 'd-flex justify-content-between pb-3')}>
              <Tabs variant="primary" isRoutesBased>
                <Tabs.Switchers className={styles.switchers}>
                  {moviesModules.map(module => (
                    <Tabs.Switcher href={`/movies/${module.id}?${query}`} key={module.id}>
                      {t(`layout.${module.title}`)}
                    </Tabs.Switcher>
                  ))}
                </Tabs.Switchers>
              </Tabs>

              <Button
                size="sm"
                variant="secondary"
                shape="round"
                as="button"
                onClick={onReset}
                className="d-none d-xl-flex"
                disabled={searchParams.size === 0}
              >
                <RemoveSVG height={24} width={24} />
                {t('common.drop')}
              </Button>
            </Col>

            <MoviesTopPanel>
              <div className={clsx(styles.extraFiltersWrapper, styles.scrollable, 'mb-3')}>
                {isLoading ? (
                  Array(5)
                    .fill(null)
                    .map((_, index) => (
                      <SkeletonTheme
                        baseColor="#0F0F12"
                        highlightColor="#47474A"
                        width={90}
                        height={30}
                        borderRadius={40}
                        key={index}
                      >
                        <Skeleton className={styles.genreSkelton} />
                      </SkeletonTheme>
                    ))
                ) : (
                  <Fragment>
                    {cinemas.map(cinema => (
                      <FormCheckbox
                        key={cinema.value}
                        label={cinemasLogo(cinema.value as MovieCardTypeEnum)}
                        variant="custom-check"
                        name={`${cinema.value}`}
                        shape="round"
                        className={styles.dropdownItem}
                        checked={(methods.getValues('cinemas') as Option[]).some(
                          item => item.value === cinema.value,
                        )}
                        onChange={() => {
                          const currentCinemas = methods.getValues('cinemas') as Option[]; // Get current cinemas
                          const cinemaExists = currentCinemas.some(
                            item => item.value === cinema.value,
                          );

                          const updatedCinemas = cinemaExists
                            ? currentCinemas.filter(item => item.value !== cinema.value)
                            : [...currentCinemas, cinema];

                          methods.setValue(FiltersRowsEnum.CINEMAS, updatedCinemas);

                          handleChange({
                            name: FiltersRowsEnum.CINEMAS,
                            value: updatedCinemas.map(item => item.value.toString()),
                          });
                        }}
                        disabled={isLoading}
                      />
                    ))}
                  </Fragment>
                )}
              </div>
              <Row>
                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti
                    isDisabled={isLoading}
                    initialValue={findInitialValue(genres, searchParams.getAll('genres'))}
                    name={FiltersRowsEnum.GENRES}
                    placeholder={t('common.genres')}
                    options={genres}
                    maxSelectableOptions={MAX_SELECTABLE_OPTIONS}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.GENRES,
                        value: (methods.getValues(FiltersRowsEnum.GENRES) as Option[]).map(genre =>
                          genre.value.toString(),
                        ),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuMultiColumns}
                  />
                </Col>
                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti={false}
                    name={FiltersRowsEnum.YEARS}
                    isDisabled={isLoading}
                    placeholder={t('common.year', {
                      count: 0,
                    })}
                    options={yearOptions}
                    initialValue={findInitialValue(yearOptions, searchParams.getAll('year'))}
                    maxSelectableOptions={1}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.YEARS,
                        value: (
                          methods.getValues(FiltersRowsEnum.YEARS) as Option
                        )?.value?.toString(),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuColumn}
                  />
                </Col>
                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti
                    name="countries"
                    isDisabled={isLoading}
                    placeholder={t('common.countries')}
                    options={countries}
                    initialValue={findInitialValue(countries, searchParams.getAll('countries'))}
                    maxSelectableOptions={COUNTRY_MAX_SELECTABLE_OPTIONS}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.COUNTRIES,
                        value: (methods.getValues(FiltersRowsEnum.COUNTRIES) as Option[]).map(
                          genre => genre.value.toString(),
                        ),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuMultiColumns}
                  />
                </Col>

                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti={false}
                    name="ratingImdb"
                    isDisabled={isLoading}
                    placeholder={t('common.ratingImdb')}
                    options={generateRatingOptions(6, 9)}
                    initialValue={findInitialValue(
                      generateRatingOptions(6, 9),
                      searchParams.getAll('ratingImdb'),
                    )}
                    maxSelectableOptions={1}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.RATING_IMDB,
                        value: (
                          methods.getValues(FiltersRowsEnum.RATING_IMDB) as Option
                        )?.value?.toString(),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuColumn}
                  />
                </Col>

                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti={false}
                    name="ratingKP"
                    isDisabled={isLoading}
                    placeholder={t('common.ratingKinopoisk')}
                    options={generateRatingOptions(6, 9)}
                    initialValue={findInitialValue(
                      generateRatingOptions(6, 9),
                      searchParams.getAll('ratingKinopoisk'),
                    )}
                    maxSelectableOptions={1}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    position="bottom-right"
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.RATING_KINOPOISK,
                        value: (
                          methods.getValues(FiltersRowsEnum.RATING_KINOPOISK) as Option
                        )?.value?.toString(),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuColumn}
                  />
                </Col>

                <Col xl={2} lg={6} xs={12} className="mb-3">
                  <Form.Select
                    isMulti
                    name="qualities"
                    isDisabled={isLoading}
                    placeholder={t('common.quality')}
                    initialValue={findInitialValue(qualities, searchParams.getAll('qualities'))}
                    options={qualities}
                    maxSelectableOptions={MAX_SELECTABLE_OPTIONS}
                    type={isExtraLarge ? 'dropdown' : 'modalDropdown'}
                    position="bottom-right"
                    onSubmitted={() => {
                      handleChange({
                        name: FiltersRowsEnum.QUALITIES,
                        value: (methods.getValues(FiltersRowsEnum.QUALITIES) as Option[]).map(
                          genre => genre.value.toString(),
                        ),
                      });
                    }}
                    selectMenuClassName={styles.selectMenuColumn}
                  />
                </Col>
              </Row>

              <div className={clsx(styles.extraFiltersWrapper, 'flex-wrap')}>
                <FormCheckbox
                  label={t('common.free')}
                  variant="checkbox"
                  name={'isFree'}
                  shape="round"
                  className={styles.dropdownItem}
                  onChange={e => {
                    handleChange({
                      name: FiltersRowsEnum.IS_FREE,
                      value: e.currentTarget.checked,
                    });
                  }}
                  disabled={isLoading}
                />
                {/* <FormCheckbox
                  label={'С субтитрами'}
                  variant="checkbox"
                  name={'isSubtitleLocales'}
                  shape="round"
                  className={styles.dropdownItem}
                  disabled={isLoading}
                /> */}
                <FormCheckbox
                  label={t('common.uzbekLanguage')}
                  variant="checkbox"
                  name={'audioTrack'}
                  shape="round"
                  className={styles.dropdownItem}
                  onChange={e => {
                    handleChange({
                      name: FiltersRowsEnum.AUDIO_TRACK,
                      value: e.currentTarget.checked ? 'uz' : '',
                    });
                  }}
                  disabled={isLoading}
                />
                <FormCheckbox
                  label={t('common.canDownload')}
                  variant="checkbox"
                  name={'isDownload'}
                  shape="round"
                  className={styles.dropdownItem}
                  onChange={e => {
                    handleChange({
                      name: FiltersRowsEnum.IS_DOWNLOAD,
                      value: e.currentTarget.checked,
                    });
                  }}
                  disabled={isLoading}
                />
              </div>
            </MoviesTopPanel>
          </Form>
        </div>
      </FormProvider>
    </Fragment>
  );
};
